'use client'

import { memo, useMemo, useState } from 'react'
import { IconTrashX } from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'

import { AddToFavouritesButton, avabilityProductCheck } from 'modules/products'
import { SeoListName } from 'shared/analytics'
import IconButton from 'shared/ui/IconButton/IconButton'
import { addTestId } from 'shared/utils/tests'

import useCartStore from '../../../store/cartStore'
import { CartItem } from '../../../types'
import useCartProductData from '../hooks/useCartItemView'
import { IS_DISABLED_MANIPULATION_CART_PROMOTION_ITEMS } from '../utils'

import AddToCartButton from './AddToCartButton'
import CartItemInfo from './CartItemInfo'
import PriceDisplay from './PriceDisplay'
import ProductAvailability from './ProductAvailability'
import RemoveLastProductModal from './RemoveLastProductModal'

import styles from './CartItemsListItem.module.scss'

interface CartItemsListItemProps {
  item: CartItem
  seoButtonPlace: string
  idxOnList: number
  listName: SeoListName
}

const CartItemsListItem = ({
  item,
  seoButtonPlace,
  idxOnList,
  listName
}: CartItemsListItemProps) => {
  const [open, setOpen] = useState(false)
  const { productsNotAvailableForDeliveryMethod, cart } = useCartStore()
  const { handleRemoveFromCartSeo, productDetails } = useCartProductData(
    item,
    listName
  )

  const quantityLimit = useMemo(
    () => avabilityProductCheck(productDetails.product)?.quantityLimit,
    [productDetails.product]
  )

  const isManipulationEnabled = useMemo(
    () => !IS_DISABLED_MANIPULATION_CART_PROMOTION_ITEMS || !item.promotionId,
    [item.promotionId]
  )

  const isNotAvailableForDeliveryMethod = useMemo(
    () =>
      productsNotAvailableForDeliveryMethod.find(
        cartItem => cartItem.product.id === item.product.id
      ),
    [productsNotAvailableForDeliveryMethod, item.product.id]
  )

  const name =
    productDetails.product.nameDescriptionShort ||
    productDetails.product.name ||
    productDetails.product.nameShort

  return (
    <div className={styles.container}>
      <div
        className={styles.wrapper}
        id={item.id}
        key={`product-card-${item.id}-${idxOnList}`}
      >
        <Image
          src={productDetails.productImage}
          alt={productDetails.product.name || 'product-image'}
          width={78}
          height={78}
        />
        <div className={styles.insideWrapper}>
          <CartItemInfo
            productPath={productDetails.productPath}
            brand={productDetails.product.brand?.name}
            name={name}
            unitDetails={productDetails.unitDetails}
            pricePerUnit={productDetails.product.pricePerUnit}
            unitPerPrice={productDetails.unitPerPrice}
            hasWeeklyAvailabilityPlan={productDetails.hasWeeklyAvailabilityPlan}
            weeklyAvailabilityPlan={productDetails.weeklyAvailabilityPlan}
            isAvailableForDeliverySlot={item.isAvailableForDeliverySlot}
            originalProduct={productDetails.product}
          />

          {item.isAvailable && !isNotAvailableForDeliveryMethod ? (
            <div className={styles.availability}>
              <div className={styles.availabilityWrapper}>
                <AddButtonWrapper className='cart-product-buttons'>
                  <div
                    className={clsx(styles.iconSectionWrapper, {
                      [styles.iconSectionWrapperManipulationEnabled]:
                        isManipulationEnabled
                    })}
                  >
                    <AddToFavouritesButton
                      product={productDetails.product}
                      idxOnList={idxOnList}
                      listName={listName}
                      isInCart
                      seoButtonPlace={seoButtonPlace}
                    />
                    {isManipulationEnabled && (
                      <>
                        <IconButton
                          className={styles.trashButton}
                          onClick={() => setOpen(true)}
                          size='small'
                          variant='transparent'
                          aria-label={`Usuń produkt ${item.product.name}`}
                          {...addTestId('cart_remove_product')}
                        >
                          <IconTrashX />
                        </IconButton>

                        <RemoveLastProductModal
                          product={productDetails.product}
                          open={open}
                          cartItemId={item.id}
                          onClose={() => setOpen(false)}
                          onQuantityChange={handleRemoveFromCartSeo}
                        />
                      </>
                    )}
                  </div>
                  {isManipulationEnabled && (
                    <div className={styles.webContent}>
                      <AddToCartButton
                        variant='primaryOutline'
                        product={productDetails.product}
                        quantityJump={1}
                        disabledQuantity={false}
                        seoButtonPlace={seoButtonPlace}
                        cartItemId={item.id}
                        listName={listName}
                        idxOnList={0}
                      />
                    </div>
                  )}
                </AddButtonWrapper>
              </div>
              <div className={styles.webContent}>
                <PriceDisplay
                  unitPrice={productDetails.unitPrice}
                  promoUnitPrice={productDetails.promoUnitPrice}
                  totalPrice={productDetails.totalPrice}
                  promoTotalPrice={productDetails.promoTotalPrice}
                />
              </div>
            </div>
          ) : (
            <>
              <ProductAvailability
                type={
                  item.isAvailable
                    ? 'not-available-in-delivery-method'
                    : 'not-available'
                }
              />
            </>
          )}
        </div>
      </div>

      <div className={styles.tabletContent}>
        {productDetails.hasWeeklyAvailabilityPlan && (
          <ProductAvailability
            type='replacements'
            hasWeeklyAvailabilityPlan={productDetails.hasWeeklyAvailabilityPlan}
            weeklyAvailabilityPlan={productDetails.weeklyAvailabilityPlan}
            isAvailableForDeliverySlot={item.isAvailableForDeliverySlot}
          />
        )}
        <div className={styles.tabletPricingWrapper}>
          <PriceDisplay
            unitPrice={productDetails.unitPrice}
            promoUnitPrice={productDetails.promoUnitPrice}
            totalPrice={productDetails.totalPrice}
            promoTotalPrice={productDetails.promoTotalPrice}
          />
          {isManipulationEnabled && (
            <AddToCartButton
              product={productDetails.product}
              quantityJump={1}
              disabledQuantity={false}
              seoButtonPlace={seoButtonPlace}
              cartItemId={item.id}
              listName={listName}
              idxOnList={0}
            />
          )}
        </div>
      </div>
      {quantityLimit < item.quantity && (
        <div className={styles.quantityLimit}>
          <p>Przekroczono limit dostępności: {quantityLimit}</p>
        </div>
      )}
      {productsNotAvailableForDeliveryMethod.find(
        cartItem => cartItem.product.id === item.product.id
      ) && (
        <div className={styles.quantityLimit}>
          <p>
            Produkt niedostępny w wybranej metodzie dostawy{' '}
            {cart?.deliverySlot?.deliveryMethod?.name}
          </p>
        </div>
      )}
    </div>
  )
}

export default memo(CartItemsListItem)

const AddButtonWrapper = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx(styles.addButtonWrapper, className)} {...props} />
)
